import { useAuthStore } from '@/stores/auth'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'

interface RequestOptions extends RequestInit {
  skipAuth?: boolean
}

async function request<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { skipAuth = false, ...fetchOptions } = options
  const authStore = useAuthStore()

  const headers: HeadersInit = {
    ...(fetchOptions.headers || {}),
  }

  // Only set Content-Type for requests with a body (avoids CORS preflight for GET)
  if (fetchOptions.method && ['POST', 'PUT', 'PATCH'].includes(fetchOptions.method)) {
    ;(headers as Record<string, string>)['Content-Type'] = 'application/json'
  }

  if (!skipAuth) {
    const token = await authStore.getToken()
    if (token) {
      ;(headers as Record<string, string>)['Authorization'] = `Bearer ${token}`
    }
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...fetchOptions,
    headers,
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
    throw new Error(error.detail || `Request failed: ${response.status}`)
  }

  if (response.status === 204) {
    return undefined as T
  }

  return response.json()
}

// Health API
export const healthApi = {
  get: () => request<HealthResponse>('/health', { skipAuth: true }),
}

// Config API
export const configApi = {
  list: (path = '') => request<ConfigItem[]>(`/admin/configs?path=${encodeURIComponent(path)}`),

  get: (path: string) => request<ConfigItem>(`/admin/configs/${path}`),

  set: (path: string, value: string, description?: string) =>
    request<ConfigItem>(`/admin/configs/${path}`, {
      method: 'PUT',
      body: JSON.stringify({ value, description }),
    }),

  delete: (path: string) =>
    request<{ status: string; deleted_count: number }>(`/admin/configs/${path}`, {
      method: 'DELETE',
    }),

  deletePreview: (path: string) =>
    request<ConfigItem[]>(`/admin/delete-preview/${path}`),
}

// API Keys API
export const apiKeysApi = {
  list: () => request<ApiKeyResponse[]>('/admin/api-keys'),

  create: (name: string) =>
    request<ApiKeyResponse>('/admin/api-keys', {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),

  revoke: (keyId: string) =>
    request<{ status: string }>(`/admin/api-keys/${keyId}`, {
      method: 'DELETE',
    }),
}

// Tenants API
export const tenantsApi = {
  list: () => request<TenantResponse[]>('/admin/tenants'),

  create: (name: string, displayName?: string) =>
    request<TenantResponse>('/admin/tenants', {
      method: 'POST',
      body: JSON.stringify({ name, display_name: displayName }),
    }),
}

// Clients API (from Clients Config API)
export const clientsApi = {
  list: () => request<string[]>('/admin/clients'),
  get: (name: string) => request<ClientConfigSummary>(`/admin/clients/${name}`),
}

// Workstations API
export const workstationsApi = {
  list: (params?: {
    client?: string
    environment?: string
    enabled?: boolean
    limit?: number
    cursor?: string
  }) => {
    const searchParams = new URLSearchParams()
    if (params?.client) searchParams.set('client', params.client)
    if (params?.environment) searchParams.set('environment', params.environment)
    if (params?.enabled !== undefined) searchParams.set('enabled', String(params.enabled))
    if (params?.limit) searchParams.set('limit', String(params.limit))
    if (params?.cursor) searchParams.set('cursor', params.cursor)
    const qs = searchParams.toString()
    return request<WorkstationListResponse>(`/admin/workstations${qs ? `?${qs}` : ''}`)
  },

  get: (machineUuid: string) =>
    request<WorkstationResponse>(`/admin/workstations/${machineUuid}`),

  create: (data: WorkstationCreateRequest) =>
    request<WorkstationResponse>('/admin/workstations', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (machineUuid: string, data: WorkstationUpdateRequest) =>
    request<WorkstationResponse>(`/admin/workstations/${machineUuid}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (machineUuid: string) =>
    request<void>(`/admin/workstations/${machineUuid}`, {
      method: 'DELETE',
    }),

  heartbeat: (machineUuid: string) =>
    request<WorkstationResponse>(`/admin/workstations/${machineUuid}/heartbeat`, {
      method: 'POST',
    }),
}

// Install API
export const installApi = {
  getCurlCommand: async (): Promise<string> => {
    const authStore = useAuthStore()
    const token = await authStore.getToken()
    if (!token) throw new Error('Not authenticated')
    const url = `${API_BASE_URL}/admin/install/rd`
    return `curl -fsSL '${url}' -H 'Authorization: Bearer ${token}' | sudo bash`
  },
}

// Types
export interface WorkstationResponse {
  machine_uuid: string
  client: string
  environment: string
  enabled: boolean
  display_name?: string
  metadata?: Record<string, string>
  registered_at?: string
  updated_at?: string
  last_seen?: string
}

export interface WorkstationListResponse {
  workstations: WorkstationResponse[]
  count: number
  cursor?: string
}

export interface WorkstationCreateRequest {
  machine_uuid: string
  client: string
  environment?: string
  enabled?: boolean
  display_name?: string
  metadata?: Record<string, string>
}

export interface WorkstationUpdateRequest {
  client?: string
  environment?: string
  enabled?: boolean
  display_name?: string
  metadata?: Record<string, string>
}

export interface ConfigItem {
  path: string
  value: string
  last_modified?: string
}

export interface ApiKeyResponse {
  key_id: string
  name: string
  created_by: string
  created_at: string
  key_prefix?: string
  key?: string
}

export interface TenantResponse {
  name: string
  display_name?: string
}

export interface HealthResponse {
  status: string
  version: string
  env: string
}

export interface ClientConfigSummary {
  name: string
  source: 'clients_api'
  config: Record<string, unknown>
  read_only: boolean
}
